import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {injectIntl} from 'react-intl';
import Util from '../../../shared/util/util';
import {Line} from 'react-chartjs-2';
import {MODAL_TYPE, showModal} from '../../../shared/components/modals/generic_modal_conductor';
import Moment from 'moment';
import * as tracking from '../../../../tracking';
import {getGradients} from '../../../../components/new-ui/project/project-budget-v3/util/ChartsUtil.js';
import {trackEvent} from '../../../../tracking/amplitude/TrackingV2';
import {canFixPeriodConflicts} from './RetainerPeriodUtil';

import {
	PeriodDataVisualsWrapper,
	TimeRegChartContainer,
	PeriodTilesGrid,
	RetainerPeriodRadialProgressBar,
	PeriodTargetTile,
	PeriodActualTile,
	PeriodConflictsTile,
	PeriodLockTile,
	PeriodEmptyTile,
	PeriodRemainingTile,
} from './ProjectRetainerPeriodVisualComponents';

class ProjectRetainerPeriodDataVisuals extends Component {
	toggleShowConflictModal() {
		tracking.trackElementClicked('Time entry conflicts');
		trackEvent('Time Entry Conflicts Modal', 'Shown');
		showModal({
			type: MODAL_TYPE.RETAINER_TIME_ENTRY_CONFLICT_HANDLING,
			period: this.props.period.node,
			conflictedTimeEntries: this.props.conflictedTimeEntries,
			inHours: this.props.inHours,
			currency: this.props.currency,
			viewer: this.props.viewer,
			useTimeApproval: this.props.useTimeApproval,
		});
	}
	render() {
		const {formatMessage} = this.props.intl;
		const {gradientStrokeFixedPrice} = getGradients();
		const data = {
			labels: [],
			datasets: [],
		};
		const {withSuffix, inHours, isFixedPrice, isTimeMaterials} = this.props;
		const period = this.props.period.node;
		// The period target is derived from periodPriceAmount, periodDifferencePriceAmount and
		// sharedPeriodDifferencePriceAmount (and, for the tiles, the period's financial numbers), all of
		// which are only readable with financial permission. Without it they resolve to null, every sum
		// below coerces them to 0, and the viewer is shown a fabricated zero target as if it were the
		// real one. Hours-based periods read the ungated hours fields and are unaffected.
		// billableActualTimeAndExpenses, which the actual/remaining tiles spend, is gated the same way.
		// A time-and-materials period is measured in hours, so the hours-aware check passes for it, but
		// its invoiceable figure stays a currency amount read from a gated field — that one value needs
		// the financial check on its own.
		const canReadPriceValues = !!this.props.hasFinancialAccess;
		const canReadPeriodTarget = inHours || canReadPriceValues;
		const financialNumbers = period.financialNumbers;
		const periodStart = Util.CreateMomentDate(period.startYear, period.startMonth, period.startDay);
		const periodEnd = Util.CreateMomentDate(period.endYear, period.endMonth, period.endDay);
		const timeRegData = new Map();
		const sortByDate = (a, b) => {
			const aDate = Util.CreateMomentDate(a.node.year, a.node.month, a.node.day);
			const bDate = Util.CreateMomentDate(b.node.year, b.node.month, b.node.day);
			return aDate.isBefore(bDate) ? -1 : 1;
		};

		//clone the array to not override it
		let graphTimeRegs = this.props.timeRegs.slice(0);

		// Add start & end date, with 0 minutes registered
		if (periodStart) {
			graphTimeRegs.push({
				node: {
					id: 'PeriodStart',
					year: periodStart.year(),
					month: periodStart.month() + 1,
					day: periodStart.date(),
					price: 0,
					billableMinutesRegistered: 0,
				},
			});
		}
		if (periodEnd) {
			graphTimeRegs.push({
				node: {
					id: 'PeriodEnd',
					year: periodEnd.year(),
					month: periodEnd.month() + 1,
					day: periodEnd.date(),
					price: 0,
					billableMinutesRegistered: 0,
				},
			});
		}
		graphTimeRegs.sort(sortByDate).forEach(timeReg => {
			const tDate = Util.CreateMomentDate(timeReg.node.year, timeReg.node.month, timeReg.node.day).format('DD MMM');
			if (timeRegData.get(tDate)) {
				timeRegData.set(
					tDate,
					timeRegData.get(tDate) + (inHours ? timeReg.node.billableMinutesRegistered / 60 : timeReg.node.price)
				);
			} else {
				timeRegData.set(tDate, inHours ? timeReg.node.billableMinutesRegistered / 60 : timeReg.node.price);
			}
		});
		const dataSetData = [];
		const dataLabels = [];
		let runningTotal = 0; // Accumulated data
		timeRegData.forEach((value, key) => {
			dataSetData.push(value + runningTotal);
			runningTotal += value;
			dataLabels.push(key);
		});
		if (dataSetData.length === 1) {
			dataSetData.push(dataSetData[0]);
			dataLabels.push(dataLabels[0]);
		}

		// The target line is a flat fill of the period target across every point, so a withheld target
		// would plot as a solid zero line rather than as an absent one. Omit the whole dataset instead.
		if (canReadPeriodTarget && (!isTimeMaterials || !!period.periodHoursAmount)) {
			data.datasets.push({
				type: 'line',
				label: formatMessage({id: 'retainer.target'}),
				borderColor: gradientStrokeFixedPrice,
				pointHoverRadius: 0,
				pointHoverBorderWidth: 2,
				pointBackgroundColor: 'transparent',
				pointHoverBorderColor: gradientStrokeFixedPrice,
				pointRadius: 0,
				fill: false,
				backgroundColor: '#fff',
				borderWidth: 2,
				data: Array(Math.max(2, dataSetData.length)).fill(
					inHours
						? period.periodHoursAmount +
								period.periodDifferenceHoursAmount +
								period.sharedPeriodDifferenceHoursAmount
						: period.periodPriceAmount +
								period.periodDifferencePriceAmount +
								period.sharedPeriodDifferencePriceAmount
				),
			});
		}
		data.datasets.push({
			label: inHours
				? formatMessage({id: 'common.total_time_entries'})
				: formatMessage({id: 'common.actual_billable_value_of_service'}),
			pointHoverRadius: 2,
			pointHoverBorderWidth: 2,
			pointBackgroundColor: '#b7ffbe',
			pointRadius: 1,
			lineTension: 0,
			fill: true,
			borderColor: '#49d156',
			backgroundColor: '#b7ffbe',
			borderWidth: 1,
			data: dataSetData,
		});

		data.labels = dataLabels;
		const options = {
			font: {
				family: 'neue-haas-grotesk-text',
			},
			plugins: {
				legend: {
					display: false,
				},
				title: {
					display: false,
				},
				tooltip: {
					mode: 'index',
					backgroundColor: '#fff',
					borderColor: '#e6e6e6',
					borderWidth: 1,
					titleColor: '#535353',
					bodyColor: '#535353',
					padding: 12,
					intersect: false,
					callbacks: {
						label: ctx => {
							const dataName = ctx.dataset.label;
							if (dataName === formatMessage({id: 'retainer.target'})) {
								return '';
							}
							return dataName + ': ' + withSuffix(parseFloat(ctx.parsed.y).toFixed(2), true);
						},
					},
				},
				datalabels: {
					display: false,
				},
			},
			responsive: true,
			maintainAspectRatio: false,
			scales: {
				y: {
					title: {
						display: true,
						text: inHours ? formatMessage({id: 'common.hours'}) : formatMessage({id: 'common.price'}),
					},
					beginAtZero: true,
					min: 0,
				},
				x: {
					ticks: {
						maxTicksLimit: data.labels.length,
					},
				},
			},
		};

		const actualHours = financialNumbers.billableActualMinutes / 60;
		const actualPrice = financialNumbers.billableActualTimeAndExpenses;
		const totalConflicted = inHours
			? this.props.conflictedTimeEntries.reduce((acc, tReg) => acc + tReg.node.billableMinutesRegistered / 60, 0)
			: this.props.conflictedTimeEntries.reduce((acc, tReg) => acc + tReg.node.price, 0);
		const periodTarget = inHours
			? financialNumbers.retainerPeriodTargetMinutes / 60
			: financialNumbers.retainerPeriodTargetPrice;
		const periodDifference = inHours
			? financialNumbers.retainerPeriodRolloverMinutes / 60
			: financialNumbers.retainerPeriodRolloverPrice;
		const periodTargetTotal = periodTarget + periodDifference;
		const progress =
			periodTargetTotal <= 0
				? 100
				: inHours
				? Math.round((actualHours / periodTargetTotal) * 100 * 10) / 10
				: Math.round((actualPrice / periodTargetTotal) * 100 * 10) / 10;
		const isOverdue = Moment().isAfter(periodEnd) && !period.periodLocked;
		const progressValue = isNaN(progress) ? 0 : progress;
		const periodAmount = inHours ? period.periodHoursAmount : period.periodPriceAmount;
		return (
			<PeriodDataVisualsWrapper>
				<TimeRegChartContainer>
					<Line options={options} data={data} />
				</TimeRegChartContainer>
				{/* A withheld target divides into a zero total, which the ring would draw as a confident
				full circle at 100%. Omit the ring rather than show a number nothing backs. */}
				{canReadPeriodTarget ? (
					<RetainerPeriodRadialProgressBar
						isFixedPrice={isFixedPrice}
						periodDifference={periodDifference}
						progressValue={progressValue}
						projectColor={this.props.projectColor}
					/>
				) : null}
				<PeriodTilesGrid>
					<PeriodTargetTile
						isFixedPrice={isFixedPrice}
						periodDifference={periodDifference}
						periodTarget={periodTarget}
						periodAmount={periodAmount}
						canReadAmount={canReadPeriodTarget}
						withSuffix={this.props.withSuffix}
					/>
					<PeriodActualTile
						isFixedPrice={isFixedPrice}
						actualHours={actualHours}
						actualPrice={actualPrice}
						canReadAmount={canReadPeriodTarget}
						withSuffix={this.props.withSuffix}
						projectColor={this.props.projectColor}
					/>
					{this.props.conflictedTimeEntries.length > 0 ? (
						<PeriodConflictsTile
							totalConflicted={totalConflicted}
							withSuffix={this.props.withSuffix}
							onFixConflicts={
								canFixPeriodConflicts(period, inHours, this.props.hasFinancialAccess)
									? this.toggleShowConflictModal.bind(this)
									: undefined
							}
						/>
					) : isOverdue && this.props.canLockPeriod ? (
						<PeriodLockTile useTimeApproval={this.props.useTimeApproval} onLockPeriod={this.props.lockPeriod} />
					) : (
						<PeriodEmptyTile />
					)}
					<PeriodRemainingTile
						isTimeMaterials={isTimeMaterials}
						isFixedPrice={isFixedPrice}
						inHours={inHours}
						actualHours={actualHours}
						actualPrice={actualPrice}
						periodTargetTotal={periodTargetTotal}
						canReadAmount={isTimeMaterials ? canReadPriceValues : canReadPeriodTarget}
						currency={this.props.currency}
						withSuffix={this.props.withSuffix}
						projectColor={this.props.projectColor}
					/>
				</PeriodTilesGrid>
			</PeriodDataVisualsWrapper>
		);
	}
}
ProjectRetainerPeriodDataVisuals.propTypes = {
	period: PropTypes.object.isRequired,
	timeRegs: PropTypes.array.isRequired,
};
export default injectIntl(ProjectRetainerPeriodDataVisuals);
